import React, { useState, useEffect } from "react";
import axios from "axios";
import "./App.css";

const API_URL = "https://ticket-backend.zlce.workers.dev/tickets";
const slackSearchUrl = "https://slack.com"; // Replace with your Slack URL

function App() {
    const [buySell, setBuySell] = useState("");
    const [tickets, setTickets] = useState([]);
    const [eventName, setEventName] = useState("");
    const [price, setPrice] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [name, setName] = useState("");
    const [numberOfTickets, setNumberOfTickets] = useState(""); // New state for number of tickets
    const [errorMessage, setErrorMessage] = useState("");
    const [availability, setAvailability] = useState({});
    const [displayMode, setDisplayMode] = useState("cards"); // State to manage display mode
    const [filterOption, setFilterOption] = useState(""); // State to manage filter option
    const [hideUnavailable, setHideUnavailable] = useState(false); // State to manage hiding unavailable tickets
    const [darkMode, setDarkMode] = useState(false); // State to manage dark mode

    useEffect(() => {
        fetchTickets();
    }, []);

    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [darkMode]);

    // Add meta viewport tag for mobile responsiveness
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0';
        document.getElementsByTagName('head')[0].appendChild(meta);
    }, []);

    const fetchTickets = async () => {
        try {
            const response = await axios.get(API_URL);
            setTickets(response.data);
            const availabilityMap = response.data.reduce((acc, ticket) => {
                acc[ticket.id] = ticket.availability === 1;
                return acc;
            }, {});
            setAvailability(availabilityMap);
        } catch (error) {
            console.error("Error fetching tickets", error);
        }
    };

    const createTicket = async (e) => {
        e.preventDefault(); // Prevent default form submission behavior
        const parsedPrice = parseFloat(price);
        const parsedNumberOfTickets = parseInt(numberOfTickets, 10);
        if (!buySell || isNaN(parsedPrice) || isNaN(parsedNumberOfTickets) || !contactEmail || !eventName || !name) {
            setErrorMessage("*All fields are required.");
            return;
        }

        const timestamp = new Date().toISOString(); // Store timestamp in UTC

        const ticketData = {
            timestamp,
            buy_sell: buySell,
            event_name: eventName,
            price: parsedPrice,
            contact_email: contactEmail,
            name: name,
            number_of_tickets: parsedNumberOfTickets, // Include number of tickets
            availability: 1 // Default to available
        };

        try {
            await axios.post(API_URL, ticketData);
            fetchTickets();
            setBuySell("");
            setEventName("");
            setPrice("");
            setContactEmail("");
            setName("");
            setNumberOfTickets(""); // Clear number of tickets
            setErrorMessage(""); // Clear error message on successful submission
        } catch (error) {
            console.error("Error creating ticket", error);
        }
    };

    const toggleAvailability = async (ticketId) => {
        const newAvailability = availability[ticketId] ? 0 : 1;
        try {
            await axios.patch(`${API_URL}/${ticketId}`, { availability: newAvailability });
            setAvailability((prev) => ({
                ...prev,
                [ticketId]: !prev[ticketId],
            }));
            fetchTickets();
        } catch (error) {
            console.error("Error updating availability", error);
            console.error("Response data:", error.response.data);
        }
    };

    const toggleDisplayMode = () => {
        setDisplayMode((prevMode) => (prevMode === "cards" ? "list" : "cards"));
    };

    const handleSortChange = (e) => {
        sortTickets(e.target.value);
    };

    const sortTickets = (option) => {
        let sortedTickets = [...tickets];
        switch (option) {
            case "eventName":
                sortedTickets.sort((a, b) => a.event_name.localeCompare(b.event_name));
                break;
            case "priceLowHigh":
                sortedTickets.sort((a, b) => a.price - b.price);
                break;
            case "priceHighLow":
                sortedTickets.sort((a, b) => b.price - a.price);
                break;
            case "postedBy":
                sortedTickets.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case "timestampNewest":
                sortedTickets.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                break;
            case "timestampOldest":
                sortedTickets.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                break;
            default:
                break;
        }
        setTickets(sortedTickets);
    };

    const filterTickets = (option) => {
        setFilterOption(option);
    };

    const getFilteredTickets = () => {
        let filteredTickets = tickets;
        if (filterOption === "buying") {
            filteredTickets = filteredTickets.filter(ticket => ticket.buy_sell === "buy");
        } else if (filterOption === "selling") {
            filteredTickets = filteredTickets.filter(ticket => ticket.buy_sell === "sell");
        }
        if (hideUnavailable) {
            filteredTickets = filteredTickets.filter(ticket => ticket.availability === 1);
        }
        return filteredTickets;
    };

    const toggleHideUnavailable = () => {
        setHideUnavailable((prev) => !prev);
    };

    const openSlackChat = (name) => {
        const slackChatUrl = `slack://user?team=T12345678&id=${encodeURIComponent(name)}`;
        navigator.clipboard.writeText(name).then(() => {
            alert(`'${name}' copied to clipboard!`);
            window.open(slackChatUrl, "_blank");
        }).catch(err => {
            console.error('Failed to copy text: ', err);
        });
    };

    const convertToLocalTime = (utcTime) => {
        const localTime = new Date(utcTime);
        return `${localTime.toLocaleDateString()} ${localTime.toLocaleTimeString()}`;
    };

    // Update form inputs to prevent zoom on mobile
    const handleFocus = (e) => {
        // Prevent zoom on focus for iOS devices
        document.body.style.fontSize = '16px';
    };

    return (
        <div className={`container ${darkMode ? 'dark-mode' : ''}`}>
            <h1 className="title">TixSwap</h1>
            <div className={`form-container ${darkMode ? 'dark-mode' : ''}`}>
                <p className="form-title">Post a Ticket</p>
                {errorMessage && <p className="error-message">{errorMessage}</p>}
                <form onSubmit={createTicket}>
                    <div className="radio-group">
                        <label className="buy-label">
                            <input type="radio" value="buy" checked={buySell === "buy"} onChange={(e) => setBuySell(e.target.value)} required />
                            <span>Buying</span> 
                        </label>
                        <label className="sell-label">
                            <input type="radio" value="sell" checked={buySell === "sell"} onChange={(e) => setBuySell(e.target.value)} required />
                            Selling
                        </label>
                    </div>
                    <input type="text" placeholder="Event Name" value={eventName} onChange={(e) => setEventName(e.target.value)} className={`input ${darkMode ? 'dark-mode' : ''}`} required onFocus={handleFocus} />
                    <input type="number" placeholder="Price" value={price} onChange={(e) => setPrice(e.target.value)} className={`input ${darkMode ? 'dark-mode' : ''}`} required onFocus={handleFocus} />
                    <input type="number" placeholder="Number of Tickets" value={numberOfTickets} onChange={(e) => setNumberOfTickets(e.target.value)} className={`input ${darkMode ? 'dark-mode' : ''}`} required onFocus={handleFocus} /> {/* New input for number of tickets */}
                    <input type="text" placeholder="Your Name" value={name} onChange={(e) => setName(e.target.value)} className={`input ${darkMode ? 'dark-mode' : ''}`} required onFocus={handleFocus} />
                    <input type="email" placeholder="Contact Email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className={`input ${darkMode ? 'dark-mode' : ''}`} required onFocus={handleFocus} />
                    <button type="submit" className="post-button">Post Ticket</button>
                </form>
            </div>
            <div className="ticket-grid-container">
                <div className="toggle-container">
                    <button onClick={toggleHideUnavailable} className="filter-button">
                        {hideUnavailable ? "Show Unavailable" : "Hide Unavailable"}
                    </button>
                    <button onClick={() => filterTickets("")} className={`filter-button show-all ${filterOption === "" ? "active" : ""}`}>Show All</button>
                    <button onClick={() => filterTickets("buying")} className={`filter-button buying ${filterOption === "buying" ? "active" : ""}`}>Buying</button>
                    <button onClick={() => filterTickets("selling")} className={`filter-button selling ${filterOption === "selling" ? "active" : ""}`}>Selling</button>
                    <h2>Sort by: </h2>
                    <select onChange={handleSortChange} className="sort-select">
                        <option value="timestampNewest">Time Posted (Newest)</option>
                        <option value="timestampOldest">Time Posted (Oldest)</option>
                        <option value="priceLowHigh">Price: Low to High</option>
                        <option value="priceHighLow">Price: High to Low</option>
                        <option value="eventName">Event Name</option>
                        <option value="postedBy">Posted By</option>
                    </select>
                    <button onClick={toggleDisplayMode} className={`toggle-button-switch ${darkMode ? 'dark-mode' : ''}`}>
                        {displayMode === "cards" ? "Switch to List View" : "Switch to Card View"}
                    </button>
                </div>
                <div className={`ticket-grid ${displayMode}`}>
                    {getFilteredTickets().map((ticket) => (
                        <div key={ticket.id} className={`ticket-card ${ticket.buy_sell === 'buy' ? (displayMode === 'cards' ? 'buy-cards' : 'buy-list') : (displayMode === 'cards' ? 'sell-cards' : 'sell-list')} ${ticket.availability === 0 ? 'not-available' : ''} ${darkMode ? 'dark-mode' : ''}`}>
                            <div className={`ticket-header ${displayMode}`}>
                                {ticket.buy_sell === 'sell' ? (
                                    <span className="sell-text">Selling</span>
                                ) : (
                                    <span className="buy-text">Buying</span>
                                )}
                            </div>
                            <div className={`ticket-body ${displayMode}`}>
                                <h3 className={`event-name ${displayMode}`}>{ticket.event_name}</h3>
                                <h2 className={`price ${displayMode}`}>${ticket.price} x {ticket.number_of_tickets}</h2>
                                <div className="posted-by-container">
                                    <p className={`posted-by-label ${displayMode}`}>Posted by:</p>
                                    <p className={`posted-by-name ${displayMode}`}>{ticket.name}</p>
                                    <p className={`timestamp ${displayMode}`}>{convertToLocalTime(ticket.timestamp)}</p>
                                </div>
                            </div>
                            <div className={`ticket-footer ${displayMode}`}>
                                <span>
                                    <button onClick={() => openSlackChat(ticket.name)} className="slack-button">Contact on Slack</button>
                                </span>
                                <span>
                                    <a href={`mailto:${ticket.contact_email}`} className="contact-button">Contact on Email</a>
                                </span>
                                <span>
                                    <button onClick={() => toggleAvailability(ticket.id)} className="toggle-button">
                                        {availability[ticket.id] ? 'Mark Unavailable' : 'Mark Available'}
                                    </button>
                                </span>
                                {ticket.availability === 0 && <div className="not-available-text">Not Available</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default App;
